import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Login.css';

interface AutoLoginProps {
    setToken: (token: string) => void;
}

function AutoLogin({ setToken }: AutoLoginProps) {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setErrorMessage(t('auth.invalidLink', 'Le lien est invalide ou a expiré.'));
            return;
        }

        (async () => {
            try {
                const response = await fetch(`${API_BASE}/auth/auto-login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });

                if (!response.ok) {
                    const data = await response.json();
                    throw new Error(data.detail || 'Auto-login failed');
                }

                const data = await response.json();
                if (data.access_token) {
                    localStorage.setItem('token', data.access_token);
                    setToken(data.access_token);
                    setStatus('success');
                    // Redirect to custom path or default to profile
                    const redirectPath = decodeURIComponent(searchParams.get('redirect') || 'dashboard/profile');
                    setTimeout(() => {
                        navigate(`/${i18n.language}/${redirectPath}`, { replace: true });
                    }, 1500);
                } else {
                    throw new Error('No token received');
                }
            } catch (err: any) {
                setStatus('error');
                if (err.message?.includes('expired')) {
                    setErrorMessage(t('auth.tokenExpired', 'Ce lien a expiré. Veuillez vous connecter avec votre email et mot de passe.'));
                } else {
                    setErrorMessage(t('auth.autoLoginFailed', 'Impossible de vous connecter automatiquement. Veuillez utiliser la page de connexion.'));
                }
            }
        })();
    }, [searchParams, navigate, i18n.language, setToken, API_BASE, t]);

    return (
        <div className="login-page">
            <div className="container py-4">
                <div className="login-card">
                    <div className="login-icon mb-3">
                        {status === 'loading' && <i className="bi bi-arrow-repeat spin-icon"></i>}
                        {status === 'success' && <i className="bi bi-check-circle text-success"></i>}
                        {status === 'error' && <i className="bi bi-exclamation-triangle text-danger"></i>}
                    </div>

                    {status === 'loading' && (
                        <>
                            <h2 className="text-center mb-2">{t('auth.autoLoginLoading', 'Connexion en cours...')}</h2>
                            <p className="text-center text-muted">
                                {t('auth.autoLoginWait', 'Veuillez patienter pendant que nous vous connectons.')}
                            </p>
                            <div className="text-center">
                                <span className="spinner-border spinner-border-sm"></span>
                            </div>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <h2 className="text-center mb-2 text-success">{t('auth.autoLoginSuccess', 'Connexion réussie !')}</h2>
                            <p className="text-center text-muted">
                                {t('auth.autoLoginRedirect', 'Vous allez être redirigé vers votre profil...')}
                            </p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <h2 className="text-center mb-2">{t('auth.autoLoginError', 'Échec de la connexion')}</h2>
                            <div className="alert alert-danger">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {errorMessage}
                            </div>
                            <Link
                                to={`/${i18n.language}/login`}
                                className="btn btn-primary w-100"
                            >
                                <i className="bi bi-box-arrow-in-right me-2"></i>
                                {t('auth.goToLogin', 'Aller à la page de connexion')}
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AutoLogin;
